/* eslint-disable react/no-children-prop */
import GoogleMap from '@/shared/GoogleMap/ui/GoogleMap';
import { ReactElement, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import * as S from './Voucher.styles';
import { ICartItem, IOrderDetail } from '@/entities/Checkout/domain/Checkout.domain';
import { SERVER_FORMAT_DATE } from 'utils/constants';
import { parse, format } from 'date-fns';

// eslint-disable-next-line sonarjs/cognitive-complexity
const Voucher = ({ order, voucher }: { order: IOrderDetail; voucher: ICartItem }): ReactElement => {
    const { format_id, customer_first_name, customer_last_name, cart_items } = order;

    const cartItems = () => {
        for (const key in cart_items) return cart_items[key];

        return '';
    };

    const items = voucher || cartItems();

    const reservationHolder = useMemo(
        () => [customer_first_name, customer_last_name].filter(Boolean).join(' '),
        [customer_first_name, customer_last_name]
    );

    const arrivalDate = useMemo(() => {
        if (items && !items.arrival_date) {
            return null;
        }
        const date = items && parse(items.arrival_date, SERVER_FORMAT_DATE, 0);
        if (!date || !date.getTime()) {
            return null;
        }
        return format(date, 'EEEE, MMMM do, yyyy');
    }, [items]);

    const arrivalTime =
        cart_items && items && (items.schedule && items.schedule.time ? items.schedule.time : null);

    const time = items.activity.id === '1453' ? 'All day admission' : arrivalTime;

    const content = (data: string) => <ReactMarkdown children={data} linkTarget="_blank" />;

    return (
        <S.Container data-test-id="Voucher">
            {items && (
                <div>
                    <S.Title>{items.trip_name}</S.Title>
                    <S.SubTitle>
                        Voucher:&nbsp; <S.VoucherNumber>{items.id}</S.VoucherNumber>
                        {(items.state === 'cancelled' || items.state === 'deleted') && (
                            <div
                                style={{
                                    fontWeight: 600,
                                    color: 'red',
                                    textTransform: 'uppercase',
                                    marginLeft: '10px',
                                }}
                            >
                                - {items.state}
                            </div>
                        )}
                    </S.SubTitle>
                    <S.VoucherInfoWrapper>
                        <S.InfoContainer>
                            <S.VoucherNameInfo>Reservation Holder</S.VoucherNameInfo>
                            <S.VoucherInfo>{reservationHolder}</S.VoucherInfo>
                        </S.InfoContainer>
                        <S.InfoContainer>
                            <S.VoucherNameInfo>Confirmation</S.VoucherNameInfo>
                            <S.VoucherInfo>{format_id}</S.VoucherInfo>
                        </S.InfoContainer>
                        {arrivalDate && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Arrival Date</S.VoucherNameInfo>
                                <S.VoucherInfo>{arrivalDate}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {time && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Departure Time</S.VoucherNameInfo>
                                <S.VoucherInfo>{time}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.product_data.agreement && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Reservation Requirements</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.product_data.agreement}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.product_data.voucher_only_text && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Arrival Instructions</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    {content(items.product_data.voucher_only_text)}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        )}

                        {items.duration && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Duration</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.duration}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}

                        {!!items.rate?.name && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Ticket Tier</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.rate.name}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}

                        <S.InfoContainer>
                            <S.VoucherNameInfo>Tickets</S.VoucherNameInfo>
                            <S.VoucherInfo> {items.booked_format}</S.VoucherInfo>
                        </S.InfoContainer>
                        <S.InfoContainer>
                            <S.VoucherNameInfo>Ticket Description</S.VoucherNameInfo>
                            <S.VoucherInfo>
                                {items &&
                                    Object.values(items.tickets).map((ticket) => (
                                        <div key={ticket.id}>
                                            <strong>{ticket.name}</strong>&nbsp;
                                            {content(ticket.description)}
                                        </div>
                                    ))}
                            </S.VoucherInfo>
                        </S.InfoContainer>
                        <>
                            {Object.values(items.additional_options).map(
                                (option) =>
                                    +option.id === 1 && (
                                        <S.InfoContainer key={option.id}>
                                            <S.VoucherNameInfo>TripFlex</S.VoucherNameInfo>
                                            <S.VoucherInfo>
                                                <strong>{option.value.price} TripFlex</strong>
                                                <br />
                                                Cancel your trip last minute for any reason and
                                                receive a full refund minus the TripFlex fee.
                                                No-shows do not apply. Claims must be filed before
                                                the departure time and must be made directly with
                                                TripShock. Please call 850-424-5125 or email
                                                customercare@tripshock.com to file a claim.
                                            </S.VoucherInfo>
                                        </S.InfoContainer>
                                    )
                            )}
                        </>
                        <S.InfoContainer>
                            <S.VoucherNameInfo>Grand Total</S.VoucherNameInfo>
                            <S.VoucherInfo>${items.grand_total}</S.VoucherInfo>
                        </S.InfoContainer>
                        {items.product_data.included && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>What’s Included</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    {content(items.product_data.included)}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.product_data.excluded && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>What to Bring</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    {content(items.product_data.excluded)}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.product_data.restrictions && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Restrictions</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    {content(items.product_data.restrictions)}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.product_data.additional && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Additional Information</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    {content(items.product_data.additional)}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {Number(items.product_data.policy_id) ? (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Cancellation Policy</S.VoucherNameInfo>
                                <S.VoucherInfo>
                                    <strong>{items.product_data.policy_title}</strong>
                                    <br />
                                    {content(
                                        items.product_data.policy_content
                                            .replace('<p>', '')
                                            .replace('</p>', '')
                                    )}
                                </S.VoucherInfo>
                            </S.InfoContainer>
                        ) : null}
                        {items.partner_name && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Provider</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.partner_name}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.partner_address && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Location</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.partner_address}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        {items.partner_phone && (
                            <S.InfoContainer>
                                <S.VoucherNameInfo>Phone</S.VoucherNameInfo>
                                <S.VoucherInfo>{items.partner_phone}</S.VoucherInfo>
                            </S.InfoContainer>
                        )}
                        <S.ContactInfoContainer>
                            <S.VoucherNameInfo>Contact Info</S.VoucherNameInfo>
                            <S.VoucherInfo>
                                If you have questions about this reservation upon arrival, please
                                contact the travel provider.
                            </S.VoucherInfo>
                        </S.ContactInfoContainer>

                        {Object.values(items?.tickets).filter(
                            (ticket) => ticket.barcodes.length > 0
                        ).length > 0 && (
                            <S.BarcodesContainer data-test-id="Barcodes">
                                {Object.values(items.tickets)
                                    .filter((ticket) => ticket.barcodes.length > 0)
                                    .map((ticket) => (
                                        <div key={ticket.id}>
                                            <div>
                                                <S.TicketType>{ticket.name}</S.TicketType>
                                                <S.BarcodesWrapper>
                                                    {ticket.barcodes.map((barcode) => (
                                                        <div key={barcode.id}>
                                                            <S.BarcodeImg
                                                                src={barcode.image_base64}
                                                                alt="Barcode"
                                                            />
                                                        </div>
                                                    ))}
                                                </S.BarcodesWrapper>
                                            </div>
                                        </div>
                                    ))}
                            </S.BarcodesContainer>
                        )}
                    </S.VoucherInfoWrapper>
                    <S.BottomWrapper data-test-id="Map">
                        {items.partner_phone && (
                            <>
                                <S.ContactInfo>Contact Info</S.ContactInfo>
                                <S.ConctactNumber>{items.partner_phone}</S.ConctactNumber>
                            </>
                        )}
                        {items.partner_address && (
                            <GoogleMap
                                height={588}
                                maxWidth={1064}
                                width={'100%'}
                                address={items.partner_address}
                            />
                        )}
                    </S.BottomWrapper>
                </div>
            )}
        </S.Container>
    );
};

export { Voucher };
export default Voucher;
